import type { TOC } from '@ember/component/template-only';
import Text from './text';
// ---------------------------------------------------------------------------
// InfoRow – simple label/value row for detail panels and cards
// ---------------------------------------------------------------------------

/**
 * Size options for the info row
 */
export type InfoRowSize = 'sm' | 'md';

interface InfoRowSignature {
  Args: {
    /**
     * Label text (left side)
     */
    label: string;
    /**
     * Value text (right side). If not provided, use default block.
     */
    value?: string;
    /**
     * Visual size of the row
     * @default "md"
     */
    size?: InfoRowSize;
  };
  Blocks: {
    /**
     * Custom value content (overrides @value)
     */
    default?: [];
  };
  Element: HTMLDivElement;
}

/**
 * InfoRow - Simple label/value row for information display
 *
 * Displays a horizontally justified pair of label and value text.
 * Commonly used in detail views, cards, and info panels.
 * Accepts a block for custom value content (e.g., `<Chip>`, badges).
 *
 * @example Basic usage
 * ```gts
 * import InfoRow from 'tio-ui/components/info-row';
 *
 * <InfoRow @label="Employer" @value="TechCorp Inc." />
 * <InfoRow @label="Start Date" @value="January 2024" />
 * ```
 *
 * @example With custom value content
 * ```gts
 * <InfoRow @label="Status">
 *   <Chip @intent="success" @size="sm">Active</Chip>
 * </InfoRow>
 * ```
 *
 * @example In a card
 * ```gts
 * <div class="tio-bg-surface rounded-xl p-4">
 *   <div class="space-y-3">
 *     <InfoRow @label="Balance" @value="$45,230" />
 *     <InfoRow @label="Interest Rate" @value="5.25%" />
 *     <InfoRow @label="Monthly Payment" @value="$450" />
 *   </div>
 * </div>
 * ```
 */
const InfoRow: TOC<InfoRowSignature> = <template>
  {{#let (if @size @size "md") as |size|}}
    <div
      data-test-info-row
      class="tio-row-between {{if (eq size 'sm') 'py-1' 'py-1.5'}}"
      ...attributes
    >
      <Text data-test-info-row-label @variant="muted" @size={{if (eq size "sm") "xs" "sm"}}>
        {{@label}}
      </Text>
      {{#if (has-block)}}
        <span data-test-info-row-value>
          {{yield}}
        </span>
      {{else if @value}}
        <Text
          data-test-info-row-value
          class="font-medium {{if (eq size 'sm') 'text-sm' 'text-base'}}"
        >
          {{@value}}
        </Text>
      {{/if}}
    </div>
  {{/let}}
</template>;

export default InfoRow;
export type { InfoRowSignature };
